import type { CompositionRow, KeyframeRow } from "@shared/utils/lottiePlaygroundModel";
import clsx from "clsx";
import type { AnimationInspectorState } from "./useAnimationInspector";

type Ins = AnimationInspectorState;

export function InspectorSelectionHeader({ row }: { row: CompositionRow }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950/80 p-3 text-xs">
      <p className="font-medium text-zinc-200">{row.title}</p>
      <p className="mt-1 text-zinc-500">
        {row.kind} · {row.detail ?? ""}
      </p>
    </div>
  );
}

export function InspectorLayerKeyframeSource({ ins }: { ins: Ins }) {
  if (!ins.isTextLayer) {
    return null;
  }
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-zinc-400">Keyframe source</span>
      <select
        className="rounded-lg border border-zinc-700 bg-zinc-900 px-2 py-2 text-zinc-100"
        data-testid="animation-playground-inspector-layer-keyframe-source"
        onChange={(e) => ins.setLayerKeyframeSource(e.target.value as "transform" | "textDoc")}
        value={ins.layerKeyframeSource}
      >
        <option value="transform">Transform</option>
        <option value="textDoc">Text document</option>
      </select>
    </label>
  );
}

export function InspectorTextFillColour({ ins }: { ins: Ins }) {
  if (!ins.isTextLayer) {
    return null;
  }
  if (ins.textFillRgb) {
    return (
      <label className="flex flex-col gap-2 text-xs">
        <span className="text-zinc-400">Text fill colour (static)</span>
        <div className="flex items-center gap-2">
          <input
            className="h-9 w-14 cursor-pointer rounded border border-zinc-700 bg-zinc-900"
            data-testid="animation-playground-inspector-text-fill-colour"
            onChange={(e) => ins.handleTextFillCommit(ins.hexToRgb01(e.target.value))}
            type="color"
            value={ins.rgb01ToHex(ins.textFillRgb)}
          />
          <span className="font-mono text-zinc-400">{ins.rgb01ToHex(ins.textFillRgb)}</span>
        </div>
        <p className="text-[10px] leading-snug text-amber-600/90">
          Writes RGB into document fc (all text keyframes when animated).
        </p>
      </label>
    );
  }
  return (
    <p className="rounded-lg border border-amber-900/50 bg-amber-950/30 px-2 py-2 text-[10px] text-amber-100/90">
      Could not read fill colour (fc) from this text document — try raw JSON or re-export from your authoring tool.
    </p>
  );
}

export function InspectorLayerTransformSelect({ ins }: { ins: Ins }) {
  if (ins.selectedRow?.kind !== "layer" || ins.layerKsOptions.length === 0) {
    return null;
  }
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-zinc-400">Transform channel</span>
      <select
        className="rounded-lg border border-zinc-700 bg-zinc-900 px-2 py-2 text-zinc-100"
        data-testid="animation-playground-inspector-layer-ks"
        onChange={(e) => ins.setLayerKsKey(e.target.value)}
        value={ins.layerKsKey}
      >
        {ins.layerKsOptions.map((opt) => (
          <option key={opt.key} value={opt.key}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function InspectorFillStrokeChannels({ ins }: { ins: Ins }) {
  const kind = ins.selectedRow?.kind;
  if (kind === "fill") {
    return (
      <label className="flex flex-col gap-1 text-xs">
        <span className="text-zinc-400">Keyframe source</span>
        <select
          className="rounded-lg border border-zinc-700 bg-zinc-900 px-2 py-2 text-zinc-100"
          data-testid="animation-playground-inspector-fill-channel"
          onChange={(e) => ins.setChannelInspect(e.target.value)}
          value={ins.channelInspect}
        >
          <option value="c">Colour</option>
          <option value="o">Opacity</option>
        </select>
      </label>
    );
  }
  if (kind === "stroke") {
    return (
      <label className="flex flex-col gap-1 text-xs">
        <span className="text-zinc-400">Keyframe source</span>
        <select
          className="rounded-lg border border-zinc-700 bg-zinc-900 px-2 py-2 text-zinc-100"
          data-testid="animation-playground-inspector-stroke-channel"
          onChange={(e) => ins.setChannelInspect(e.target.value)}
          value={ins.channelInspect}
        >
          <option value="c">Colour</option>
          <option value="o">Opacity</option>
          <option value="w">Width</option>
        </select>
      </label>
    );
  }
  return null;
}

export function InspectorFillColourBlock({ ins }: { ins: Ins }) {
  if (ins.selectedRow?.kind !== "fill" || !ins.fillRgb || !ins.paths.fillColour) {
    return null;
  }
  return (
    <label className="flex flex-col gap-2 text-xs">
      <span className="text-zinc-400">Fill colour (static)</span>
      <div className="flex items-center gap-2">
        <input
          className="h-9 w-14 cursor-pointer rounded border border-zinc-700 bg-zinc-900"
          data-testid="animation-playground-inspector-fill-colour"
          onChange={(e) => ins.commitColour(ins.paths.fillColour, ins.hexToRgb01(e.target.value))}
          type="color"
          value={ins.rgb01ToHex(ins.fillRgb)}
        />
        <span className="font-mono text-zinc-400">{ins.rgb01ToHex(ins.fillRgb)}</span>
      </div>
      <p className="text-[10px] leading-snug text-amber-600/90">
        Writes a static RGB triplet; removes colour keyframes on this fill if present.
      </p>
    </label>
  );
}

export function InspectorFillOpacityBlock({ ins }: { ins: Ins }) {
  if (ins.selectedRow?.kind !== "fill" || !ins.paths.fillOpacity) {
    return null;
  }
  return (
    <div className="flex flex-col gap-1 text-xs">
      <span className="text-zinc-400">Fill opacity % (static)</span>
      {ins.fillOpacityStatic != null ? (
        <input
          className="rounded-lg border border-zinc-700 bg-zinc-900 px-2 py-2 text-zinc-100"
          data-testid="animation-playground-inspector-fill-opacity"
          max={100}
          min={0}
          onChange={(e) => ins.handleFillOpacityCommit(Number(e.target.value))}
          type="number"
          value={Math.round(ins.fillOpacityStatic)}
        />
      ) : (
        <p className="rounded-lg border border-zinc-800 bg-zinc-950 px-2 py-2 text-[10px] text-zinc-500">
          Opacity is animated or non-scalar — use keyframes below.
        </p>
      )}
    </div>
  );
}

export function InspectorStrokeColourBlock({ ins }: { ins: Ins }) {
  if (ins.selectedRow?.kind !== "stroke" || !ins.strokeRgb || !ins.paths.strokeColour) {
    return null;
  }
  return (
    <label className="flex flex-col gap-2 text-xs">
      <span className="text-zinc-400">Stroke colour (static)</span>
      <div className="flex items-center gap-2">
        <input
          className="h-9 w-14 cursor-pointer rounded border border-zinc-700 bg-zinc-900"
          data-testid="animation-playground-inspector-stroke-colour"
          onChange={(e) => ins.commitColour(ins.paths.strokeColour, ins.hexToRgb01(e.target.value))}
          type="color"
          value={ins.rgb01ToHex(ins.strokeRgb)}
        />
        <span className="font-mono text-zinc-400">{ins.rgb01ToHex(ins.strokeRgb)}</span>
      </div>
      <p className="text-[10px] leading-snug text-amber-600/90">
        Writes a static RGB triplet; removes colour keyframes on this stroke if present.
      </p>
    </label>
  );
}

export function InspectorStrokeWidthBlock({ ins }: { ins: Ins }) {
  if (ins.selectedRow?.kind !== "stroke" || !ins.paths.strokeWidth) {
    return null;
  }
  return (
    <div className="flex flex-col gap-1 text-xs">
      <span className="text-zinc-400">Stroke width (static)</span>
      {ins.strokeWidthStatic != null ? (
        <input
          className="rounded-lg border border-zinc-700 bg-zinc-900 px-2 py-2 text-zinc-100"
          data-testid="animation-playground-inspector-stroke-width"
          min={0}
          onChange={(e) => ins.handleStrokeWidthCommit(Number(e.target.value))}
          step={0.5}
          type="number"
          value={ins.strokeWidthStatic}
        />
      ) : (
        <p className="rounded-lg border border-zinc-800 bg-zinc-950 px-2 py-2 text-[10px] text-zinc-500">
          Width is animated — use keyframes below (Keyframe source → Width).
        </p>
      )}
    </div>
  );
}

export function InspectorStrokeOpacityBlock({ ins }: { ins: Ins }) {
  if (ins.selectedRow?.kind !== "stroke" || !ins.paths.strokeOpacity) {
    return null;
  }
  return (
    <div className="flex flex-col gap-1 text-xs">
      <span className="text-zinc-400">Stroke opacity % (static)</span>
      {ins.strokeOpacityStatic != null ? (
        <input
          className="rounded-lg border border-zinc-700 bg-zinc-900 px-2 py-2 text-zinc-100"
          max={100}
          min={0}
          onChange={(e) => ins.handleStrokeOpacityCommit(Number(e.target.value))}
          type="number"
          value={Math.round(ins.strokeOpacityStatic)}
        />
      ) : (
        <p className="rounded-lg border border-zinc-800 bg-zinc-950 px-2 py-2 text-[10px] text-zinc-500">
          Opacity is animated or non-scalar — use keyframes below.
        </p>
      )}
    </div>
  );
}

export function InspectorKeyframeTable({ label, rows }: { label: string; rows: KeyframeRow[] }) {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs font-medium text-zinc-400">Keyframes · {label}</p>
      <div className="max-h-48 overflow-auto rounded-lg border border-zinc-800 bg-zinc-950">
        <table className="w-full border-collapse text-left text-[11px]">
          <thead className="sticky top-0 bg-zinc-900 text-zinc-500">
            <tr>
              <th className="px-2 py-1.5 font-medium">#</th>
              <th className="px-2 py-1.5 font-medium">Frame</th>
              <th className="px-2 py-1.5 font-medium">Value</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((kf) => (
              <tr className="border-t border-zinc-800/80" key={`${kf.index}-${kf.frame}`}>
                <td className="px-2 py-1 font-mono text-zinc-500">{kf.index}</td>
                <td className="px-2 py-1 font-mono text-zinc-300">{kf.frame}</td>
                <td className={clsx("px-2 py-1 font-mono text-zinc-400")}>{kf.valueSummary}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function InspectorRawSelectionJson({ node }: { node: unknown }) {
  if (!node || typeof node !== "object") {
    return null;
  }
  return (
    <details className="rounded-lg border border-zinc-800 bg-zinc-950/50 text-xs">
      <summary className="cursor-pointer px-3 py-2 text-zinc-400">Raw JSON (selection)</summary>
      <pre className="max-h-40 overflow-auto border-t border-zinc-800 p-2 font-mono text-[10px] text-zinc-500">
        {JSON.stringify(node, null, 2)}
      </pre>
    </details>
  );
}
